// Money is always an integer number of minor units plus the currency's
// exponent; it is never handled as a floating-point amount.

export interface CurrencyLike {
  code: string;
  exponent: number;
  symbol?: string | null;
}

/** Formats minor units for display, e.g. (10000, NGN exp 2) -> "₦100.00". */
export function formatMoney(minor: number, cur: CurrencyLike, opts: { sign?: boolean } = {}): string {
  const neg = minor < 0;
  let digits = String(Math.abs(Math.trunc(minor)));
  let frac = "";
  if (cur.exponent > 0) {
    digits = digits.padStart(cur.exponent + 1, "0");
    frac = "." + digits.slice(-cur.exponent);
    digits = digits.slice(0, -cur.exponent);
  }
  const whole = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const body = cur.symbol ? `${cur.symbol}${whole}${frac}` : `${whole}${frac} ${cur.code}`;
  if (neg) return `-${body}`;
  return opts.sign && minor > 0 ? `+${body}` : body;
}

/**
 * Parses a user-typed major-unit amount ("1,250.50") into minor units using
 * string arithmetic only. Returns null for invalid input or too many decimals.
 */
export function parseMoney(input: string, exponent: number): number | null {
  const s = input.replace(/[,\s_]/g, "").trim();
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const [whole, frac = ""] = s.split(".");
  if (frac.length > exponent) return null;
  const minor = BigInt(whole + frac.padEnd(exponent, "0"));
  if (minor > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(minor);
}

/** Compact display for large counts: 1234 -> "1,234". */
export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}
